import type { BlogArticle } from '../../types/blog'
import { BLOG_DATE_MODIFIED, BLOG_DATE_PUBLISHED } from './blogCommon'

export const chooseDentistArticle: BlogArticle = {
  slug: 'how-to-choose-dentist-angel',
  theme: 'teal',
  datePublished: BLOG_DATE_PUBLISHED,
  dateModified: BLOG_DATE_MODIFIED,
  category: { en: 'Tips', ua: 'Поради', ru: 'Советы', ro: 'Sfaturi' },
  readTime: { en: '7 min read', ua: '7 хв читання', ru: '7 мин чтения', ro: '7 min citire' },
  seoTitle: {
    en: 'How to Choose a Dentist in Angel, Islington | Dentico Clinic',
    ua: 'Як обрати стоматолога в Angel, Islington | Dentico Clinic',
    ru: 'Как выбрать стоматолога в Angel, Islington | Dentico Clinic',
    ro: 'Cum alegeți un dentist în Angel, Islington | Dentico Clinic',
  },
  seoDescription: {
    en: 'Practical tips for choosing a private dentist in Angel, Islington, including qualifications, reviews, communication, technology and transparent pricing.',
    ua: 'Практичні поради, як обрати приватного стоматолога в Angel, Islington: кваліфікація, відгуки, комунікація, обладнання і прозорі ціни.',
    ru: 'Практические советы, как выбрать частного стоматолога в Angel, Islington: квалификация, отзывы, коммуникация, оборудование и прозрачные цены.',
    ro: 'Sfaturi practice pentru alegerea unui dentist privat în Angel, Islington: calificări, recenzii, comunicare, tehnologie și prețuri transparente.',
  },
  h1: {
    en: 'How to choose a dentist in Angel, Islington',
    ua: 'Як обрати стоматолога в Angel, Islington',
    ru: 'Как выбрать стоматолога в Angel, Islington',
    ro: 'Cum alegeți un dentist în Angel, Islington',
  },
  cardTitle: {
    en: 'How to choose a dentist in Angel, Islington',
    ua: 'Як обрати стоматолога в Angel, Islington',
    ru: 'Как выбрать стоматолога в Angel, Islington',
    ro: 'Cum alegeți un dentist în Angel, Islington',
  },
  cardExcerpt: {
    en: 'What to check before booking: GDC registration, genuine reviews, clear communication, modern equipment and transparent prices.',
    ua: 'Що перевірити перед записом: реєстрація в GDC, справжні відгуки, зрозуміла комунікація, сучасне обладнання і прозорі ціни.',
    ru: 'Что проверить перед записью: регистрация в GDC, настоящие отзывы, понятная коммуникация, современное оборудование и прозрачные цены.',
    ro: 'Ce verificați înainte de programare: înregistrarea GDC, recenzii reale, comunicare clară, echipamente moderne și prețuri transparente.',
  },
  intro: {
    en: 'Angel and the wider Islington area have many dental practices, which makes choosing one harder rather than easier. A good dentist is not only technically skilled but also explains things clearly, is honest about costs and makes you feel comfortable coming back. These practical checks will help you choose with confidence in 2026.',
    ua: 'В Angel і загалом в Islington багато стоматологічних практик, тому обрати одну не легше, а складніше. Хороший стоматолог не лише технічно вправний, а й зрозуміло пояснює, чесно говорить про вартість і створює відчуття, що повертатися комфортно. Ці практичні перевірки допоможуть упевнено обрати клініку у 2026 році.',
    ru: 'В Angel и в целом в Islington много стоматологических практик, поэтому выбрать одну не легче, а сложнее. Хороший стоматолог не только технически умел, но и понятно объясняет, честно говорит о стоимости и создаёт ощущение, что возвращаться комфортно. Эти практические проверки помогут уверенно выбрать клинику в 2026 году.',
    ro: 'Angel și zona Islington au multe cabinete stomatologice, ceea ce face alegerea mai dificilă, nu mai ușoară. Un dentist bun nu este doar competent tehnic, ci și explică clar, este onest în privința costurilor și vă face să vă simțiți confortabil să reveniți. Aceste verificări practice vă ajută să alegeți cu încredere în 2026.',
  },
  sections: [
    {
      heading: {
        en: 'Six things to check before you book',
        ua: 'Шість речей, які варто перевірити перед записом',
        ru: 'Шесть вещей, которые стоит проверить перед записью',
        ro: 'Șase lucruri de verificat înainte de programare',
      },
      blocks: [
        {
          type: 'steps',
          items: [
            {
              title: { en: 'GDC registration', ua: 'Реєстрація в GDC', ru: 'Регистрация в GDC', ro: 'Înregistrarea GDC' },
              text: {
                en: 'Every dentist practising in the UK must be registered with the General Dental Council. You can check any dentist on the public GDC register online.',
                ua: 'Кожен стоматолог, що практикує у Великій Британії, має бути зареєстрований у General Dental Council. Перевірити будь-якого лікаря можна в публічному реєстрі GDC онлайн.',
                ru: 'Каждый стоматолог, практикующий в Великобритании, должен быть зарегистрирован в General Dental Council. Проверить любого врача можно в публичном реестре GDC онлайн.',
                ro: 'Orice dentist care profesează în Marea Britanie trebuie să fie înregistrat la General Dental Council. Puteți verifica orice medic în registrul public GDC online.',
              },
            },
            {
              title: { en: 'Genuine patient reviews', ua: 'Справжні відгуки пацієнтів', ru: 'Настоящие отзывы пациентов', ro: 'Recenzii reale ale pacienților' },
              text: {
                en: 'Look for recent, detailed reviews on Google that mention communication and aftercare, not just star ratings. You can read [Dentico Clinic patient reviews](/reviews).',
                ua: 'Шукайте свіжі, детальні відгуки в Google, де згадуються комунікація і подальший супровід, а не лише зірки. Можна прочитати [відгуки пацієнтів Dentico Clinic](/reviews).',
                ru: 'Ищите свежие, подробные отзывы в Google, где упоминаются коммуникация и дальнейшее сопровождение, а не только звёзды. Можно прочитать [отзывы пациентов Dentico Clinic](/reviews).',
                ro: 'Căutați recenzii recente și detaliate pe Google, care menționează comunicarea și îngrijirea ulterioară, nu doar numărul de stele. Puteți citi [recenziile pacienților Dentico Clinic](/reviews).',
              },
            },
            {
              title: { en: 'Clear communication', ua: 'Зрозуміла комунікація', ru: 'Понятная коммуникация', ro: 'Comunicare clară' },
              text: {
                en: 'You should understand your diagnosis and options. If English is not your first language, a clinic that speaks your language can make a real difference.',
                ua: 'Ви маєте розуміти свій діагноз і варіанти лікування. Якщо англійська не рідна, клініка, яка говорить вашою мовою, може суттєво допомогти.',
                ru: 'Вы должны понимать свой диагноз и варианты лечения. Если английский не родной, клиника, говорящая на вашем языке, может существенно помочь.',
                ro: 'Trebuie să înțelegeți diagnosticul și opțiunile. Dacă engleza nu este limba maternă, o clinică ce vorbește limba dumneavoastră poate face o diferență reală.',
              },
            },
            {
              title: { en: 'Transparent pricing', ua: 'Прозорі ціни', ru: 'Прозрачные цены', ro: 'Prețuri transparente' },
              text: {
                en: 'A trustworthy practice publishes its fees and gives a written plan before treatment. Compare with our [dental price list](/prices).',
                ua: 'Надійна практика публікує ціни і надає письмовий план до початку лікування. Порівняйте з нашим [прайс-листом](/prices).',
                ru: 'Надёжная практика публикует цены и предоставляет письменный план до начала лечения. Сравните с нашим [прайс-листом](/prices).',
                ro: 'Un cabinet de încredere își publică tarifele și oferă un plan scris înainte de tratament. Comparați cu [lista noastră de prețuri](/prices).',
              },
            },
            {
              title: { en: 'Modern diagnostics', ua: 'Сучасна діагностика', ru: 'Современная диагностика', ro: 'Diagnostic modern' },
              text: {
                en: 'Digital X-rays, intraoral scanning and 3D imaging help dentists diagnose accurately and plan treatment more predictably.',
                ua: 'Цифрові знімки, інтраоральне сканування і 3D-діагностика допомагають точно ставити діагноз і передбачуваніше планувати лікування.',
                ru: 'Цифровые снимки, интраоральное сканирование и 3D-диагностика помогают точно ставить диагноз и более предсказуемо планировать лечение.',
                ro: 'Radiografiile digitale, scanarea intraorală și imagistica 3D ajută la diagnosticarea precisă și la planificarea mai previzibilă a tratamentului.',
              },
            },
            {
              title: { en: 'Range of services', ua: 'Спектр послуг', ru: 'Спектр услуг', ro: 'Gama de servicii' },
              text: {
                en: 'A practice offering prevention, restorative, cosmetic and urgent care means you do not need to be referred elsewhere for most treatment. See our [dental services](/services).',
                ua: 'Практика, що надає профілактику, лікування, естетичну та невідкладну допомогу, означає, що для більшості процедур не доведеться шукати іншу клініку. Дивіться наші [стоматологічні послуги](/services).',
                ru: 'Практика, оказывающая профилактику, лечение, эстетическую и неотложную помощь, означает, что для большинства процедур не придётся искать другую клинику. Смотрите наши [стоматологические услуги](/services).',
                ro: 'Un cabinet care oferă prevenție, tratamente restaurative, estetice și de urgență înseamnă că nu veți fi trimis în altă parte pentru majoritatea tratamentelor. Vedeți [serviciile noastre stomatologice](/services).',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Start with a dental examination',
        ua: 'Почніть зі стоматологічного огляду',
        ru: 'Начните со стоматологического осмотра',
        ro: 'Începeți cu o examinare stomatologică',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'The best way to judge a new practice is a first visit. A thorough [dental examination](/prices#consultations) should include checks of your teeth, gums and soft tissues, X-rays where needed, and a clear explanation of any findings — without pressure to start treatment immediately.',
            ua: 'Найкращий спосіб оцінити нову практику — перший візит. Ретельний [стоматологічний огляд](/prices#consultations) має включати перевірку зубів, ясен і м’яких тканин, знімки за потреби і зрозуміле пояснення результатів — без тиску негайно починати лікування.',
            ru: 'Лучший способ оценить новую практику — первый визит. Тщательный [стоматологический осмотр](/prices#consultations) должен включать проверку зубов, дёсен и мягких тканей, снимки при необходимости и понятное объяснение результатов — без давления немедленно начинать лечение.',
            ro: 'Cea mai bună metodă de a evalua un cabinet nou este prima vizită. O [examinare stomatologică](/prices#consultations) amănunțită ar trebui să includă verificarea dinților, gingiilor și țesuturilor moi, radiografii la nevoie și o explicație clară a constatărilor — fără presiune de a începe imediat tratamentul.',
          },
        },
      ],
    },
    {
      heading: {
        en: 'Why patients choose Dentico Clinic in Angel',
        ua: 'Чому пацієнти обирають Dentico Clinic в Angel',
        ru: 'Почему пациенты выбирают Dentico Clinic в Angel',
        ro: 'De ce aleg pacienții Dentico Clinic în Angel',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'Dentico Clinic is a private practice a short walk from Angel station. Our team speaks English, Ukrainian, Russian and Romanian, our prices are published openly, and we give written plans before treatment. You can find out more [about our clinic and team](/about) or [contact us](/contact) to arrange a visit.',
            ua: 'Dentico Clinic — приватна клініка за кілька хвилин пішки від станції Angel. Наша команда говорить українською, англійською, російською та румунською, ціни опубліковані відкрито, а письмовий план ми надаємо до початку лікування. Детальніше [про клініку і команду](/about) або [зв’яжіться з нами](/contact), щоб домовитися про візит.',
            ru: 'Dentico Clinic — частная клиника в нескольких минутах ходьбы от станции Angel. Наша команда говорит на русском, английском, украинском и румынском, цены опубликованы открыто, а письменный план мы предоставляем до начала лечения. Подробнее [о клинике и команде](/about) или [свяжитесь с нами](/contact), чтобы договориться о визите.',
            ro: 'Dentico Clinic este un cabinet privat la câteva minute de mers pe jos de stația Angel. Echipa noastră vorbește română, engleză, ucraineană și rusă, prețurile sunt publicate deschis și oferim planuri scrise înainte de tratament. Aflați mai multe [despre clinică și echipă](/about) sau [contactați-ne](/contact) pentru a stabili o vizită.',
          },
        },
      ],
    },
  ],
  faqs: [
    {
      q: { en: 'How do I check if a dentist is qualified?', ua: 'Як перевірити кваліфікацію стоматолога?', ru: 'Как проверить квалификацию стоматолога?', ro: 'Cum verific dacă un dentist este calificat?' },
      a: {
        en: 'Search the dentist’s name on the General Dental Council online register. All practising UK dentists must appear there.',
        ua: 'Знайдіть ім’я лікаря в онлайн-реєстрі General Dental Council. Усі практикуючі стоматологи Великої Британії мають бути там.',
        ru: 'Найдите имя врача в онлайн-реестре General Dental Council. Все практикующие стоматологи Великобритании должны быть там.',
        ro: 'Căutați numele medicului în registrul online al General Dental Council. Toți dentiștii care profesează în Marea Britanie trebuie să apară acolo.',
      },
    },
    {
      q: { en: 'How often should I see a dentist?', ua: 'Як часто треба відвідувати стоматолога?', ru: 'Как часто нужно посещать стоматолога?', ro: 'Cât de des ar trebui să merg la dentist?' },
      a: {
        en: 'Your dentist will recommend an interval based on your oral health, often every 6–12 months for check-ups and hygiene.',
        ua: 'Лікар порекомендує інтервал залежно від стану порожнини рота — часто кожні 6–12 місяців для огляду і гігієни.',
        ru: 'Врач порекомендует интервал в зависимости от состояния полости рта — часто каждые 6–12 месяцев для осмотра и гигиены.',
        ro: 'Medicul va recomanda un interval în funcție de sănătatea orală, adesea la fiecare 6–12 luni pentru control și igienizare.',
      },
    },
    {
      q: { en: 'Do I need a referral to see a private dentist?', ua: 'Чи потрібне направлення до приватного стоматолога?', ru: 'Нужно ли направление к частному стоматологу?', ro: 'Am nevoie de trimitere pentru un dentist privat?' },
      a: {
        en: 'No. You can book directly with a private practice, including online.',
        ua: 'Ні. До приватної практики можна записатися напряму, зокрема онлайн.',
        ru: 'Нет. В частную практику можно записаться напрямую, в том числе онлайн.',
        ro: 'Nu. Vă puteți programa direct la un cabinet privat, inclusiv online.',
      },
    },
    {
      q: { en: 'Can I get treatment in my own language in Angel?', ua: 'Чи можна лікуватися рідною мовою в Angel?', ru: 'Можно ли лечиться на родном языке в Angel?', ro: 'Pot primi tratament în limba mea în Angel?' },
      a: {
        en: 'At Dentico Clinic, consultations are available in English, Ukrainian, Russian and Romanian.',
        ua: 'У Dentico Clinic консультації доступні українською, англійською, російською та румунською.',
        ru: 'В Dentico Clinic консультации доступны на русском, английском, украинском и румынском.',
        ro: 'La Dentico Clinic, consultațiile sunt disponibile în română, engleză, ucraineană și rusă.',
      },
    },
    {
      q: { en: 'What should happen at a first visit?', ua: 'Що має відбуватися на першому візиті?', ru: 'Что должно происходить на первом визите?', ro: 'Ce ar trebui să se întâmple la prima vizită?' },
      a: {
        en: 'A full examination, X-rays if needed, a clear explanation of findings and a written plan with costs if treatment is recommended.',
        ua: 'Повний огляд, знімки за потреби, зрозуміле пояснення результатів і письмовий план з вартістю, якщо рекомендоване лікування.',
        ru: 'Полный осмотр, снимки при необходимости, понятное объяснение результатов и письменный план со стоимостью, если рекомендовано лечение.',
        ro: 'O examinare completă, radiografii la nevoie, o explicație clară a constatărilor și un plan scris cu costuri dacă se recomandă tratament.',
      },
    },
  ],
  readAlso: [
    {
      to: '/about',
      label: { en: 'About Dentico Clinic', ua: 'Про Dentico Clinic', ru: 'О Dentico Clinic', ro: 'Despre Dentico Clinic' },
    },
    {
      to: '/reviews',
      label: { en: 'Patient Reviews', ua: 'Відгуки пацієнтів', ru: 'Отзывы пациентов', ro: 'Recenziile pacienților' },
    },
  ],
}
